import { createContext, PropsWithChildren, useMemo } from 'react'

import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'

import { SubmissionStatuses } from '@enums'
import { IApplicationState } from '@store'
import { selectSubmissionsFilters } from '@store/campaigns'
import { selectCampaignId, selectLocationsearchAsObject } from '@store/router'

export interface ISubmissionListFilterContextProps {
  filter: string
  sortBy?: string
  declinedFilter?: string
  isFilterLicenseRelated?: boolean
}

export const SubmissionListFilterContext = createContext({} as ISubmissionListFilterContextProps)

// Base component that can accept props for testing
export const SubmissionListFilterProvider: React.FC<
  PropsWithChildren<Partial<ISubmissionListFilterContextProps>>
> = ({
  children,
  filter: filterProp,
  sortBy: sortByProp,
  declinedFilter: declinedFilterProp,
  isFilterLicenseRelated: isFilterLicenseRelatedProp,
}) => {
  const contextProps: ISubmissionListFilterContextProps = useMemo(
    () => ({
      filter: filterProp ?? '',
      sortBy: sortByProp,
      declinedFilter: declinedFilterProp,
      isFilterLicenseRelated: isFilterLicenseRelatedProp,
    }),
    [filterProp, sortByProp, declinedFilterProp, isFilterLicenseRelatedProp],
  )

  return (
    <SubmissionListFilterContext.Provider value={contextProps}>
      {children}
    </SubmissionListFilterContext.Provider>
  )
}

// Connected component that uses hooks
const ConnectedSubmissionListFilterProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const match = useRouteMatch()
  const searchParams = useSelector(selectLocationsearchAsObject)
  const campaignId = useSelector(() => selectCampaignId(match) as number)

  const parsedFilter = searchParams.filter && decodeURIComponent(searchParams.filter)
  const sortBy = searchParams.sort_by && decodeURIComponent(searchParams.sort_by)
  const declinedFilter =
    searchParams.declined_filter && decodeURIComponent(searchParams.declined_filter)

  const filters = useSelector((state: IApplicationState) =>
    selectSubmissionsFilters(state, campaignId),
  )
  const filter = filters.includes(parsedFilter) ? parsedFilter : filters[0]
  const isFilterLicenseRelated = [
    SubmissionStatuses.Licensed,
    SubmissionStatuses.LicenseRequest,
  ].includes(filter as SubmissionStatuses)

  return (
    <SubmissionListFilterProvider
      filter={filter}
      sortBy={sortBy}
      declinedFilter={declinedFilter}
      isFilterLicenseRelated={isFilterLicenseRelated}
    >
      {children}
    </SubmissionListFilterProvider>
  )
}

export default ConnectedSubmissionListFilterProvider
